import { useRequiredContext } from "@redotech/react-util/context";
import { useTranslation } from "@redotech/redo-i18n";
import CodeIcon from "@redotech/redo-icons/code-02_filled.svg";
import FunctionIcon from "@redotech/redo-icons/lightning-01.svg";
import FunctionFilledIcon from "@redotech/redo-icons/lightning-01_filled.svg";
import LogsIcon from "@redotech/redo-icons/list.svg";
import LogsFilledIcon from "@redotech/redo-icons/list_filled.svg";
import { MerchantRoutesContext } from "@redotech/redo-merchant-app-common/merchant-routes";
import { UserContext } from "@redotech/redo-merchant-app-common/user";
import { LinkPrimaryTab } from "@redotech/redo-web/arbiter-components/navigation/primary/redo-primary-nav";
import { PageNavigationEntry } from "@redotech/redo-web/arbiter-components/navigation/redo-side-navigation";
import { SecondaryNavigationData } from "@redotech/redo-web/arbiter-components/navigation/secondary/redo-secondary-nav";
import {
  RedoSecondaryNavItem,
  useNavTreeNode,
} from "@redotech/redo-web/arbiter-components/navigation/secondary/redo-secondary-nav-item";
import { memo, useContext } from "react";

// Developer portal is restricted to these specific accounts while it's in early
// development; widen this (or swap for a permission/flag) before a broader rollout.
const ALLOWED_DEVELOPER_EMAILS = new Set([
  "bcarper@getredo.com",
  "dev@getredo.com",
]);

export function useDeveloperNavigation(): PageNavigationEntry | null {
  const { t } = useTranslation();
  const routes = useRequiredContext(MerchantRoutesContext);
  const user = useContext(UserContext);

  if (!user?.email || !ALLOWED_DEVELOPER_EMAILS.has(user.email.toLowerCase())) {
    return null;
  }

  const primaryTab: LinkPrimaryTab = {
    id: "developer",
    hasNotification: false,
    UnfilledIcon: CodeIcon,
    FilledIcon: CodeIcon,
    type: "link",
    href: routes.developerFunctionsHref,
    pathRoot: routes.developerBaseUrl,
    friendlyName: t("Developer", {
      context: "primary navigation section for developer tools",
    }),
  };

  const secondaryNavigationData: SecondaryNavigationData = {
    id: "developer",
    name: t("Developer", {
      context: "primary navigation section for developer tools",
    }),
    NavContent: SecondaryDeveloperNavigation,
  };

  return {
    primaryTab,
    secondaryNavigationData,
    isActiveProduct: true,
    isCore: true,
    pinLast: true,
  };
}

const SecondaryDeveloperNavigation = memo(
  function SecondaryDeveloperNavigation() {
    const { t } = useTranslation();
    const { id: nodeId, TreeContext } = useNavTreeNode({
      friendlyName: t("Developer", {
        context: "primary navigation section for developer tools",
      }),
    });

    return (
      <TreeContext.Provider value={nodeId}>
        <DeveloperFunctionsItem />
        <DeveloperLogsItem />
      </TreeContext.Provider>
    );
  },
);

const DeveloperFunctionsItem = memo(function DeveloperFunctionsItem() {
  const { t } = useTranslation();
  const routes = useRequiredContext(MerchantRoutesContext);

  return (
    <RedoSecondaryNavItem
      action={{ type: "link", href: routes.developerFunctionsHref }}
      FilledIcon={FunctionFilledIcon}
      friendlyName={t("Functions", {
        context: "developer navigation label for deployed code functions",
      })}
      id="developer-functions"
      UnfilledIcon={FunctionIcon}
    />
  );
});

const DeveloperLogsItem = memo(function DeveloperLogsItem() {
  const { t } = useTranslation();
  const routes = useRequiredContext(MerchantRoutesContext);

  return (
    <RedoSecondaryNavItem
      action={{ type: "link", href: routes.developerLogsHref }}
      FilledIcon={LogsFilledIcon}
      friendlyName={t("Logs", {
        context: "developer navigation label for application log output",
      })}
      id="developer-logs"
      UnfilledIcon={LogsIcon}
    />
  );
});
